import { appGetState } from 'legacy/lut/store'

import { getTokens } from 'core/classes/helpers/StorageHelper'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { HttpHeaders, Rest } from 'core/classes/http'

import type { AcknowledgmentData, AcknowledgmentOffering } from '../model/types'

import { recordAcknowledgmentCall } from './recordAcknowledgmentCall'

type FetchAcknowledgmentStatusDataParams = {
  partnerId: string
  fYear: number
  /** `null` reads a distributor's acknowledgment, one for every offering. */
  programType: AcknowledgmentOffering
  /** The place the rows are read for, shown in the network data window. */
  debugSource?: string | null
}

type TCommitmentDataResponse = { Rows?: AcknowledgmentData[] } | null | undefined

const toRows = (response: TCommitmentDataResponse) => response?.Rows ?? []

const ACKNOWLEDGMENT_ROWS_TARGET = 'useAcknowledgmentStatusData · items'

const fetchCommitmentData = async ({
  ownerId,
  partnerId,
  fYear,
  programType,
  debugSource,
}: {
  ownerId: string
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  debugSource?: string | null
}): Promise<AcknowledgmentData[]> => {
  const url = proxy('/webservice/621/sp/v3/spJBPCommitmentData/call')
  const [accessToken] = getTokens(ownerId)
  const headers = new HttpHeaders()
  if (accessToken) {
    headers.add('Authorization', `Bearer ${accessToken}`)
  }
  const payload = {
    'p.PartnerId': partnerId,
    'p.FYear': fYear,
    'p.ProgramType': programType,
  }
  return recordAcknowledgmentCall(
    {
      name: 'spJBPCommitmentData',
      method: 'POST',
      url,
      body: payload,
      target: ACKNOWLEDGMENT_ROWS_TARGET,
      source: debugSource,
    },
    (): Promise<TCommitmentDataResponse> => Rest.post({ url, payload, headers }),
    toRows
  )
}

const initCommitmentData = async ({
  ownerId,
  partnerId,
  fYear,
  programType,
  debugSource,
}: {
  ownerId: string
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  debugSource?: string | null
}): Promise<AcknowledgmentData[]> => {
  if (!partnerId || programType === '' || !fYear) {
    return []
  }
  const url = proxy('/webservice/621/sp/v3/spJBPCommitmentDataInit/call')
  const [accessToken] = getTokens(ownerId)
  const headers = new HttpHeaders()
  if (accessToken) {
    headers.add('Authorization', `Bearer ${accessToken}`)
  }
  const payload = {
    'p.PartnerId': partnerId,
    'p.FYear': fYear,
    'p.ProgramType': programType,
  }
  return recordAcknowledgmentCall(
    {
      name: 'spJBPCommitmentDataInit',
      method: 'POST',
      url,
      body: payload,
      target: ACKNOWLEDGMENT_ROWS_TARGET,
      source: debugSource,
    },
    (): Promise<TCommitmentDataResponse> => Rest.post({ url, payload, headers }),
    toRows
  )
}

export const fetchAcknowledgmentStatusData = async ({
  partnerId,
  fYear,
  programType,
  debugSource,
}: FetchAcknowledgmentStatusDataParams): Promise<AcknowledgmentData[]> => {
  if (!partnerId || !fYear || programType === '') {
    return []
  }

  const ownerId = appGetState().app.ownerId

  const items = await fetchCommitmentData({ ownerId, partnerId, fYear, programType, debugSource })

  if (items.length > 0) {
    return items
  }

  return initCommitmentData({ ownerId, partnerId, fYear, programType, debugSource })
}
